// TODO [GitHub Issue]: Use this map
export class ExtendedMap<K, V> implements Map<K, V> {

  private static UndefinedException = (key) => `The value for the key ${key} is undefined!`

  private _options: {
    throwOnUndefinedValue?: boolean
  }
  private _map: Map<K, V>

  constructor(options: any = {}) {
    this._options.throwOnUndefinedValue = options.throwOnUndefinedValue || false
    this._map = new Map<K, V>()
  }

  public getOrSet(key: K, value: V|{(key: K, map: Map<K, V>): V}): V {
    let result: V
    try {
      result = this.get(key)
    } finally {
      if (typeof result === 'undefined') {
        if (value instanceof Function) {
          this.set(key, value(key, this))
        } else {
          this.set(key, value)
        }
        result = this.get(key)
      }
    }
    return result
  }

  /**
   * Applies `processorFn` to the value stored under `key`
   */
  public process(key: K, processorFn: {(value: V, index: K, map: Map<K, V>): any}): any {
    let value = this.get(key)
    if (typeof value === 'undefined' && this._options.throwOnUndefinedValue) {
      throw ExtendedMap.UndefinedException(key)
    }
    return processorFn(value, key, <Map<K, V>> this)
  }

  /**
   * @Overridden
   */
  public get size(): number {
    return this._map.size
  }

  /**
   * @Overridden
   */
  public [Symbol.iterator]() {
    return this._map[Symbol.iterator]()
  }

  /**
   * @Overridden
   */
  public get [Symbol.toStringTag]() {
    return this._map[Symbol.toStringTag]
  }

  /**
   * @Overridden
   */
  public clear(): void {
    this._map.clear()
  }

  /**
   * @Overridden
   */
  public delete(key: K) {
    return this._map.delete(key)
  }

  /**
   * @Overridden
   */
  public entries() {
    return this._map.entries()
  }

  /**
   * @Overridden
   */
  public forEach(callback: {(value: V, index: K, map: Map<K, V>): void}, thisArg?: any) {
    return this._map.forEach((v, i, m) => { callback.call(thisArg || this, v, i, this) }, this)
  }

  /**
   * @Overridden
   */
  public has(key: K) {
    return this._map.has(key)
  }

  /**
   * @Overridden
   */
  public keys() {
    return this._map.keys()
  }

  /**
   * @Overridden
   */
  public values() {
    return this._map.values()
  }

  /**
   * @Overridden
   * @param key Key to be retrieved
   * @param defaultValue Value to be returned if the value for the specified key is undefined
   * or defaultValueDeterminationStrategy (if specified) evaluates the value to false
   * @param defaultValueDeterminationStrategy Strategy used to check whether `defaultValue` should be used or not
   */
  public get(key: K, defaultValue: V = undefined, defaultValueDeterminationStrategy: {(value: V): boolean} = undefined): V {
    let value = this._map.get(key)
    if (typeof defaultValueDeterminationStrategy === 'function') {
      value = defaultValueDeterminationStrategy(value) ? defaultValue : value
    } else if (typeof value === 'undefined') {
      value = defaultValue
    }
    if (typeof value === 'undefined' && this._options.throwOnUndefinedValue) {
      throw ExtendedMap.UndefinedException(key)
    }
    return value
  }

  /**
   * @Overridden
   */
  public set(key: K, value: V) {
    if (typeof value === 'undefined' && this._options.throwOnUndefinedValue) {
      throw ExtendedMap.UndefinedException(key)
    }
    this._map.set(key, value)
    return this
  }

  /**
   * @Overridden
   */
  public toJSON() {
    return JSON.stringify(this._map)
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/helpers/extended-map.ts